import type {
  GeneratedImageResult,
  ImageGenParams,
  ImageProvider,
  ImageProviderConfig,
} from "@roqeet/core";
import { base64ToBlob } from "./openai-image.js";

export class ImagenImageProvider implements ImageProvider {
  readonly kind = "imagen";
  readonly supportsProgress = false;

  async generate(
    params: ImageGenParams,
    config: ImageProviderConfig,
    _onProgress?: (pct: number, previewBlob?: Blob) => void,
  ): Promise<GeneratedImageResult[]> {
    const base = (config.endpoint ?? "https://generativelanguage.googleapis.com/v1beta").replace(
      /\/+$/,
      "",
    );
    const model = config.defaultModel ?? "imagen-4.0-generate-001";
    const extra = params.extra ?? {};
    const headers: Record<string, string> = {
      "content-type": "application/json",
      ...(config.extraHeaders ?? {}),
    };

    const parameters: Record<string, unknown> = {
      sampleCount: clampCount(params.count ?? 1),
      ...(aspectRatio(params) ? { aspectRatio: aspectRatio(params) } : {}),
      ...(extra["imageSize"] ? { imageSize: extra["imageSize"] } : {}),
      ...(extra["personGeneration"] ? { personGeneration: extra["personGeneration"] } : {}),
      ...(extra["language"] ? { language: extra["language"] } : {}),
      ...(isRecord(extra["parameters"]) ? extra["parameters"] : {}),
    };

    const body = {
      instances: [{ prompt: params.prompt }],
      parameters,
    };
    const init: RequestInit = { method: "POST", headers, body: JSON.stringify(body) };
    if (params.signal) init.signal = params.signal;

    const res = await fetch(`${base}/models/${encodeURIComponent(model)}:predict`, init);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Imagen image generation failed (HTTP ${res.status}): ${text.slice(0, 400)}`);
    }
    const json = (await res.json()) as ImagenResponse;
    const images = extractImagenImages(json);
    if (images.length === 0) throw new Error("Imagen response did not contain generated image bytes.");

    return images.map((image) => ({
      blob: base64ToBlob(image.data, image.mimeType ?? "image/png"),
      provenance: {
        provider: config.providerId ?? this.kind,
        model,
        prompt: params.prompt,
        ...(params.seed != null ? { seed: params.seed } : {}),
        ...(params.width ? { width: params.width } : {}),
        ...(params.height ? { height: params.height } : {}),
        rawParams: parameters,
      },
    }));
  }
}

interface ImagenResponse {
  predictions?: {
    bytesBase64Encoded?: string;
    mimeType?: string;
    image?: {
      bytesBase64Encoded?: string;
      imageBytes?: string;
      mimeType?: string;
    };
  }[];
}

function extractImagenImages(json: ImagenResponse): { data: string; mimeType?: string }[] {
  const out: { data: string; mimeType?: string }[] = [];
  for (const prediction of json.predictions ?? []) {
    const data =
      prediction.bytesBase64Encoded ??
      prediction.image?.bytesBase64Encoded ??
      prediction.image?.imageBytes;
    if (!data) continue;
    out.push({ data: stripDataUrl(data), mimeType: prediction.mimeType ?? prediction.image?.mimeType });
  }
  return out;
}

function aspectRatio(params: ImageGenParams): string | undefined {
  if (!params.width || !params.height) return undefined;
  const ratio = params.width / params.height;
  if (Math.abs(ratio - 1) < 0.02) return "1:1";
  if (Math.abs(ratio - 4 / 3) < 0.02) return "4:3";
  if (Math.abs(ratio - 3 / 4) < 0.02) return "3:4";
  if (Math.abs(ratio - 16 / 9) < 0.02) return "16:9";
  if (Math.abs(ratio - 9 / 16) < 0.02) return "9:16";
  return undefined;
}

function clampCount(count: number): number {
  return Math.max(1, Math.min(4, Math.floor(count)));
}

function stripDataUrl(value: string): string {
  const comma = value.indexOf(",");
  return value.startsWith("data:") && comma >= 0 ? value.slice(comma + 1) : value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}
